import { useMemo } from "react";
import { CheckCircle2, AlertCircle, AlertTriangle } from "lucide-react";
import { validatePhone, formatPhoneDisplay, normalisePhone } from "../lib/contact";

/**
 * Phone input with live validation indicator.
 *
 * Wave 27.7 contract (Dirk 2026-02-20):
 *   • `value`           — canonical E.164-no-plus (e.g. "27769012331") or "".
 *   • `onChange(next)`  — called with the CANONICAL form, never the raw
 *                          mid-typing string. Form state never holds garbage.
 *   • On blur          — the display is rewritten to the pretty form,
 *                         so the user immediately sees +27 76 901 2331.
 *   • Invalid input     — red border + helper text + onChange("") so the
 *                          parent's required-check kicks in.
 *
 * Use as a drop-in replacement for every `<input type="tel">` in the app.
 */
export default function PhoneInput({
  value = "",
  onChange,
  placeholder = "076 901 2331 or +27 76 901 2331",
  disabled,
  testId = "phone-input",
  showPreview = true,
  className = "",
}) {
  const v = useMemo(() => validatePhone(value), [value]);

  const ringClass =
    v.state === "valid"
      ? "border-emerald-500/40 focus:border-emerald-500"
      : v.state === "warn"
        ? "border-amber-500/40 focus:border-amber-500"
        : v.state === "invalid"
          ? "border-rose-500/40 focus:border-rose-500"
          : "";

  const handleChange = (e) => {
    const raw = e.target.value;
    // Push canonical to parent. If raw is invalid mid-typing we still push
    // the best-effort canonical so the visible value matches state.
    const canonical = normalisePhone(raw);
    onChange(canonical);
  };

  const handleBlur = (e) => {
    // On blur, if the result is valid, replace the displayed text with
    // the pretty form so the user gets the visual confirmation.
    const raw = e.target.value;
    if (validatePhone(raw).state === "valid") {
      const canonical = normalisePhone(raw);
      onChange(canonical);  // canonical to state
      // Display update happens via parent re-render with `formatPhoneDisplay`.
    }
  };

  return (
    <div className={`space-y-1 ${className}`}>
      <div className="relative">
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={value ? formatPhoneDisplay(value) : ""}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={`hv-input w-full pr-8 ${ringClass}`}
          data-testid={testId}
        />
        {v.state !== "empty" && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2" data-testid={`${testId}-state`}>
            {v.state === "valid" && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
            {v.state === "warn" && <AlertTriangle className="w-4 h-4 text-amber-400" />}
            {v.state === "invalid" && <AlertCircle className="w-4 h-4 text-rose-400" />}
          </span>
        )}
      </div>
      {v.message && (
        <div
          className={`text-[10px] uppercase tracking-wider ${
            v.state === "valid"
              ? "text-emerald-300"
              : v.state === "warn"
                ? "text-amber-300"
                : "text-rose-300"
          }`}
          data-testid={`${testId}-msg`}
        >
          {v.message}
        </div>
      )}
      {showPreview && v.state === "valid" && (
        <div className="text-[10px] font-mono text-[#647087]" data-testid={`${testId}-preview`}>
          → will dial as {formatPhoneDisplay(value)}
        </div>
      )}
    </div>
  );
}
